import type { FieldValues } from 'react-hook-form';
import { Checkbox } from '@florencelabs/checkbox';
import {
  ComboBox,
  MobileComboBox,
  ComboBoxItem,
} from '@florencelabs/combo-box';
import {
  DateField,
  calendarDateToYMD,
  convertToISOFormat,
  customDateFieldValidation,
  customTextFieldValidation,
  translateValidation,
} from '@florencelabs/date-field';
import { Radio } from '@florencelabs/radio';
import { Select, SelectItem, MobileSelect } from '@florencelabs/select';
import { TextArea } from '@florencelabs/text-area';
import { TextFieldInput } from '@florencelabs/text-field-input';
import { TooltipButton } from '@florencelabs/tooltip';
import { CalendarDate, parseDate } from '@internationalized/date';
import React, { ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { FormBlockType, FormFieldConfig, HookFormProps, Option } from './types';

interface FieldBlockProps<FormValues extends FieldValues>
  extends HookFormProps<FormValues> {
  field: FormFieldConfig<FormValues>;
}

export const FormFieldBlock = <FormValues extends FieldValues>({
  control,
  field,
}: FieldBlockProps<FormValues>): ReactElement | null => {
  const { t } = useTranslation();

  const { name, label } = field;

  const required = !!field.validation?.required;
  const validation =
    field.validation && translateValidation(field.validation, t);

  const isDesktop = useIsDesktop();

  const renderComboBoxItem = (option: Option, isMobile: boolean) => {
    return (
      <ComboBoxItem
        id={option.value}
        key={option.value}
        value={option}
        textValue={t(option.label)}
        isMobile={isMobile}
      >
        {t(option.label)}
      </ComboBoxItem>
    );
  };

  switch (field.type) {
    case FormBlockType.DATE:
      return (
        <Controller
          control={control}
          name={name}
          rules={{
            ...validation,
            validate: (value: string, formValues: FormValues) =>
              customDateFieldValidation<FormValues>({
                value,
                validation,
                formValues,
                t,
                validations: field.validations,
              }),
          }}
          render={({
            field: { onChange, onBlur, value, ref },
            fieldState: { invalid, error },
          }) => (
            <DateField
              ref={ref}
              testId={`schedule-dateinput-${name}`}
              name={name}
              shouldForceLeadingZeros={true}
              onChange={(dateValue: CalendarDate | null) => {
                onChange(calendarDateToYMD(dateValue));
              }}
              onBlur={onBlur}
              isRequired={!!required}
              isInvalid={invalid}
              errorMessage={error?.message && t(error.message)}
              label={t(label)}
              value={value ? parseDate(convertToISOFormat(value)) : null}
            />
          )}
        />
      );
    case FormBlockType.TEXTAREA:
      return (
        <Controller
          control={control}
          name={name}
          rules={validation}
          render={({
            field: { onChange, onBlur, value, ref },
            fieldState: { invalid, error },
          }) => (
            <TextArea
              ref={ref}
              testIdPrefix={'schedule'}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              textAreaClassName={'min-h-[200px] resize-y'}
              showScrollHints={true}
              label={t(field.label)}
              maxLength={validation?.maxLength as number}
              placeholder={field.placeholder && t(field.placeholder)}
              isRequired={!!field.validation?.required}
              optionalText={t('schedule.optional')}
              isInvalid={invalid}
              errorMessage={error?.message && t(error.message)}
            />
          )}
        />
      );

    case FormBlockType.SELECT: {
      return (
        <Controller
          control={control}
          name={name}
          rules={{ ...validation }}
          render={({
            field: { onChange, onBlur, value },
            fieldState: { invalid, error },
          }) => {
            const tooltipVariant = isDesktop ? field.tooltipVariant : undefined;

            if (isDesktop) {
              return (
                <Select
                  testIdPrefix={'schedule'}
                  name={name}
                  items={field.options}
                  onSelectionChange={onChange}
                  onBlur={onBlur}
                  label={t(label)}
                  aria-label={t(label)}
                  isRequired={!!required}
                  isInvalid={invalid}
                  errorMessage={error?.message && t(error.message)}
                  tooltipText={field.tooltip && t(field.tooltip)}
                  optionalText={t('schedule.optional')}
                  clearable={false}
                  maxHeight={300}
                  selectedKey={value}
                  tooltipVariant={tooltipVariant}
                  buttonClassName={
                    'data-[focus-visible]:ring-2 data-[focus-visible]:ring-blue-600 data-[focus-visible]:ring-offset-2'
                  }
                >
                  {field.options.map((option) => (
                    <SelectItem
                      data-testid={`${name}-option-${option.value}`}
                      id={option.value}
                      key={option.value}
                    >
                      {t(option.label)}
                    </SelectItem>
                  ))}
                </Select>
              );
            }

            return (
              <div className={'relative'}>
                <MobileSelect
                  testIdPrefix={'schedule'}
                  name={name}
                  items={field.options}
                  onSelectionChange={onChange}
                  onBlur={onBlur}
                  label={t(label)}
                  aria-label={t(label)}
                  isRequired={!!required}
                  isInvalid={invalid}
                  errorMessage={error?.message && t(error.message)}
                  optionalText={t('schedule.optional')}
                  clearable={false}
                  selectedKey={value}
                >
                  {field.options.map((option) => (
                    <SelectItem
                      data-testid={`${name}-option-${option.value}`}
                      id={option.value}
                      key={option.value}
                      isMobile={true}
                    >
                      {t(option.label)}
                    </SelectItem>
                  ))}
                </MobileSelect>
                {field.tooltip ? (
                  <TooltipButton
                    label={t(label)}
                    className={'top-8'}
                    displayType={'mobileTablet'}
                    ariaLabel={'More information'}
                    tooltipText={t(field.tooltip)}
                  />
                ) : null}
              </div>
            );
          }}
        />
      );
    }

    case FormBlockType.COMBOBOX: {
      return (
        <Controller
          control={control}
          name={name}
          rules={validation}
          render={({
            field: { onChange, onBlur, value },
            fieldState: { invalid, error },
          }) => {
            if (isDesktop) {
              return (
                <ComboBox
                  testIdPrefix={'schedule'}
                  name={name}
                  // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
                  onSelectionChange={onChange}
                  onBlur={onBlur}
                  label={t(label)}
                  isRequired={!!required}
                  isInvalid={invalid}
                  errorMessage={error?.message && t(error.message)}
                  optionalText={t('schedule.optional')}
                  clearable={false}
                  // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
                  selectedKey={value}
                  inputClassName={
                    'data-[focus-visible]:ring-2 data-[focus-visible]:ring-blue-600 data-[focus-visible]:ring-offset-2'
                  }
                >
                  {field.options.map((option) =>
                    renderComboBoxItem(option, false),
                  )}
                </ComboBox>
              );
            }

            return (
              <MobileComboBox
                testIdPrefix={'schedule'}
                name={name}
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
                onSelectionChange={onChange}
                onBlur={onBlur}
                label={t(label)}
                isRequired={!!required}
                isInvalid={invalid}
                errorMessage={error?.message && t(error.message)}
                optionalText={t('schedule.optional')}
                clearable={false}
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
                selectedKey={value}
              >
                {field.options.map((option) =>
                  renderComboBoxItem(option, true),
                )}
              </MobileComboBox>
            );
          }}
        />
      );
    }

    case FormBlockType.TEXT:
      return (
        <Controller
          control={control}
          name={name}
          rules={{
            ...validation,
            validate: (value: string, formValues: FormValues) =>
              customTextFieldValidation<FormValues>({
                value,
                validation,
                formValues,
                t,
                validations: field.validations,
              }),
          }}
          render={({
            field: { onChange, onBlur, value, ref },
            fieldState: { invalid, error },
          }) => (
            <TextFieldInput
              ref={ref}
              testIdPrefix={'schedule'}
              name={name}
              mask={field.mask?.pattern}
              maskPlaceholder={field.mask?.placeholder}
              onChange={onChange}
              onBlur={(e) => {
                onChange(e.target.value.trim());
                onBlur();
              }}
              isRequired={!!required}
              isInvalid={invalid}
              errorMessage={error?.message && t(error.message)}
              optionalText={t('schedule.optional')}
              label={t(label)}
              maxLength={validation?.maxLength as number}
              value={value}
              type={field.inputType}
            />
          )}
        />
      );

    case FormBlockType.RADIO:
      return (
        <fieldset className={'flex flex-col gap-3'}>
          {label && (
            <legend className={'mb-8 font-bold text-neutral-950'}>
              {t(label)}{' '}
              {required && (
                <>
                  <span aria-hidden={'true'}>*</span>
                  <span className={'sr-only'}>
                    {`(${t('schedule.required')})`}
                  </span>
                </>
              )}
            </legend>
          )}
          <Controller
            control={control}
            name={name}
            rules={validation}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                {field.options.map((option) => (
                  <Radio
                    testId={`schedule-radio-${name}-${option.value}`}
                    key={option.value}
                    id={`${name}-${option.value}`}
                    name={name}
                    value={option.value}
                    checked={value === option.value}
                    aria-required={required || undefined}
                    aria-describedby={
                      error?.message ? `${name}-error` : undefined
                    }
                    onChange={(e) => {
                      onChange(e.target.value);
                      field.onOptionSelected?.(option);
                    }}
                  >
                    {t(option.label)}
                  </Radio>
                ))}
                {error?.message ? (
                  <div
                    id={`${name}-error`}
                    className={'text-body-small-medium text-red-600'}
                  >
                    {t(error.message)}
                  </div>
                ) : null}
              </>
            )}
          />
        </fieldset>
      );

    case FormBlockType.CHECKBOX:
      return (
        <div className={'flex flex-col gap-3'}>
          <Controller
            control={control}
            name={name}
            rules={validation}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                <Checkbox
                  className={'flex items-center'}
                  testId={`schedule-checkbox-${name}`}
                  name={name}
                  id={name}
                  checked={value}
                  onChange={(e) => {
                    onChange(e.target.checked);
                  }}
                  size={'lg'}
                  aria-describedby={
                    [
                      field.ariaDescribedBy,
                      error?.message ? `${name}-error` : undefined,
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  aria-required={required || undefined}
                >
                  <span className={'font-medium text-neutral-950'}>
                    {t(label)} {required && <span aria-hidden={'true'}>*</span>}
                  </span>
                </Checkbox>
                {error?.message ? (
                  <div
                    id={`${name}-error`}
                    className={'text-body-small-medium text-red-600'}
                  >
                    {t(error.message)}
                  </div>
                ) : null}
              </>
            )}
          />
        </div>
      );
    default:
      return null;
  }
};
